<template>
  <div class="absolute inset-0 pointer-events-none z-10">
    <!-- SVG overlay for gradient connecting lines -->
    <svg 
      class="absolute inset-0 w-full h-full pointer-events-none z-20"
      :viewBox="`0 0 ${circleSize} ${circleSize}`"
    >
      <GradientPipsOverlay
        v-for="item in wledLightsWithGradients"
        :key="`gradient-${item.light.id}`"
        :light="item.light"
        :effect-settings="getEffectSettings(item.light.id)"
        :circle-radius="circleRadius"
        :circle-center-x="circleSize / 2"
        :circle-center-y="circleSize / 2"
        :is-selected="isSelected(item.light)"
        :is-inherited="isLightInherited(item.light.id)"
        :inherited-from-scene-name="getInheritedFromSceneName(item.light.id)"
        class="pointer-events-auto"
        @stop-drag-start="(e, light, stopId, color) => emit('gradientStopDragStart', e, light, stopId, color)"
        @stop-click="(light, stopId) => emit('gradientStopClick', light, stopId)"
      />
    </svg>
    
    <!-- Single Light Pips (non-stacked, non-gradient) -->
    <template v-for="item in singlesWithPositions" :key="`light-${item.light.id}`">
      <LightPipSingle
        v-if="!hasGradient(item.light)"
        :item="item.light"
        :x="item.position.x"
        :y="item.position.y"
        :circle-radius="circleRadius"
        :circle-center-x="circleSize / 2"
        :circle-center-y="circleSize / 2"
        :is-selected="isSelected(item.light)"
        :is-inherited="item.isInherited"
        :inherited-from="item.inheritedFrom"
        class="pointer-events-auto"
        @drag-start="emit('pipMouseDown', $event, item.light)"
        @click="emit('pipClick', item.light)"
      />
    </template>
    
    <!-- Stacked Light Pips -->
    <StackedPip
      v-for="stack in stacks"
      :key="stack.id"
      :stack="stack"
      :circle-radius="circleRadius"
      :circle-center-x="circleSize / 2"
      :circle-center-y="circleSize / 2"
      :is-selected="isStackSelected(stack)"
      :selected-light-ids="selectedLightIds"
      class="pointer-events-auto"
      @drag-start="(e, lights) => emit('stackDragStart', e, lights)"
      @click="(lights) => emit('stackClick', lights)"
      @light-drag-start="(e, light) => emit('pipMouseDown', e, light)"
      @light-click="(light) => emit('pipClick', light)"
      @disband="(lights) => emit('disbandStack', lights)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import LightPipSingle from './LightPipSingle.vue'
import StackedPip from './StackedPip.vue'
import GradientPipsOverlay from './GradientPipsOverlay.vue'
import type { Light, WledEffectSettings } from '@/stores/lights'
import type { LightWithPosition, StackedGroup } from '@/composables/useLightPositioning'
import { useSceneLightConfigsStore } from '@/stores/sceneLightConfigs'

interface Props {
  singlesWithPositions: LightWithPosition[]
  stacks: StackedGroup[]
  circleSize: number
  circleRadius: number
  sceneId: string
  isSelected: (item: Light) => boolean
  selectedLightIds: Set<string>
}

const props = defineProps<Props>()

const sceneLightConfigsStore = useSceneLightConfigsStore()

const emit = defineEmits<{
  pipMouseDown: [event: MouseEvent, item: Light]
  pipClick: [item: Light]
  gradientStopDragStart: [event: MouseEvent, light: Light, stopId: string, color: { hue: number; saturation: number; brightness: number }]
  gradientStopClick: [light: Light, stopId: string]
  stackDragStart: [event: MouseEvent, lights: Light[]]
  stackClick: [lights: Light[]]
  disbandStack: [lights: Light[]]
}>()

// Check if any light in a stack is selected
function isStackSelected(stack: StackedGroup): boolean {
  return stack.lights.some(item => props.selectedLightIds.has(item.light.id))
}

// Combine singles and stacks for gradient detection
const allLightsWithPositions = computed(() => {
  const fromStacks = props.stacks.flatMap(s => s.lights)
  return [...props.singlesWithPositions, ...fromStacks]
})

// Get wled_effect_settings for a light from scene config (with inheritance)
function getEffectSettings(lightId: string): WledEffectSettings | null | undefined {
  const resolvedConfig = sceneLightConfigsStore.getResolvedConfig(props.sceneId, lightId)
  return resolvedConfig?.wled_effect_settings
}

// Check if a light config is inherited
function isLightInherited(lightId: string): boolean {
  const resolvedConfig = sceneLightConfigsStore.getResolvedConfig(props.sceneId, lightId)
  return resolvedConfig?.is_inherited ?? false
}

// Get the scene name that a light's config is inherited from
function getInheritedFromSceneName(lightId: string): string | null {
  const resolvedConfig = sceneLightConfigsStore.getResolvedConfig(props.sceneId, lightId)
  return resolvedConfig?.inherited_from_scene_name ?? null
}

// Check if a light has gradient settings (from scene config)
// Returns false for effectId 0 (Solid) even if gradient data exists
function hasGradient(item: Light): boolean {
  const settings = getEffectSettings(item.id)
  // Don't show gradient pips for Solid effect (id 0)
  if (settings?.effectId === 0) return false
  return !!(settings?.gradient?.stops && settings.gradient.stops.length >= 2)
}

// Get all WLED lights with gradients (from all lights, not just singles)
const wledLightsWithGradients = computed(() => {
  return allLightsWithPositions.value.filter(item => {
    return hasGradient(item.light)
  }) as { light: Light; position: { x: number; y: number }; isInherited: boolean; inheritedFrom: string }[]
})
</script>
